import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Upload, Clock, Info, Menu, X,
  Globe, LayoutDashboard, LogOut, UserRound, CreditCard, KeyRound
} from 'lucide-react';
import { AriaLogo } from './AriaLogo';
import { TrialBanner } from './TrialBanner';
import { ChangePasswordDialog } from './ChangePasswordDialog';
import { Button } from './ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { useAuth } from '../context/AuthContext';
import { useBilling } from '../context/BillingContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Footer } from './Footer';

const Layout = ({ children }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const { billing } = useBilling();
  const [portalLoading, setPortalLoading] = useState(false);
  const [pwdDialogOpen, setPwdDialogOpen] = useState(false);

  const openPortal = async () => {
    if (portalLoading) return;
    setPortalLoading(true);
    try {
      const res = await api.post('/billing/portal', { origin_url: window.location.origin });
      if (res.data?.url) {
        window.open(res.data.url, '_blank', 'noopener');
      }
    } catch (e) {
      if (e?.response?.status === 404) {
        toast.error(t('billing.portal_no_sub'));
      } else {
        toast.error(t('billing.portal_error'));
      }
    } finally {
      setPortalLoading(false);
    }
  };

  const currentLang = i18n.language;

  const toggleLanguage = () => {
    const newLang = currentLang === 'fr' ? 'en' : 'fr';
    i18n.changeLanguage(newLang);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/', label: t('nav.upload'), icon: Upload },
    { path: '/history', label: t('nav.history'), icon: Clock },
    { path: '/dashboard', label: t('nav.dashboard'), icon: LayoutDashboard },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const initials = (user?.name || user?.email || '?')
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div className="app-shell grain-overlay">
      {/* Top Navigation Bar */}
      <header className="fixed top-0 left-0 right-0 z-50 h-16 bg-[hsl(210,18%,7%)]/90 backdrop-blur-md border-b border-border">
        <div className="h-full max-w-[1440px] mx-auto px-4 md:px-6 flex items-center justify-between">
          {/* Logo */}
          <div
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => navigate('/')}
            data-testid="app-logo"
          >
            <AriaLogo size={36} className="shrink-0" />
            <div className="hidden sm:block">
              <h1 className="text-sm font-semibold text-foreground leading-none">
                {t('app.title')}
              </h1>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {t('app.subtitle')}
              </p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map(({ path, label, icon: Icon }) => (
              <Button
                key={path}
                variant={isActive(path) ? 'secondary' : 'ghost'}
                size="sm"
                className={`gap-2 ${isActive(path) ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                onClick={() => navigate(path)}
                data-testid={`nav-${path === '/' ? 'upload' : path.slice(1)}`}
              >
                <Icon size={16} />
                {label}
              </Button>
            ))}
          </nav>

          {/* Right side controls */}
          <div className="flex items-center gap-2">
            {/* Language Toggle */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={toggleLanguage}
                    className="gap-1.5 text-muted-foreground hover:text-foreground"
                    data-testid="language-toggle"
                  >
                    <Globe size={16} />
                    <span className="text-xs font-semibold uppercase">
                      {currentLang}
                    </span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {currentLang === 'fr' ? 'Switch to English' : 'Passer en Fran\u00e7ais'}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {/* User menu */}
            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="w-9 h-9 rounded-full bg-cyan-500/15 ring-1 ring-cyan-500/30 text-cyan-300 text-xs font-bold flex items-center justify-center hover:bg-cyan-500/25 transition-colors duration-150"
                    data-testid="user-menu-trigger"
                  >
                    {initials}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[hsl(210,18%,10%)] border-border w-52">
                  <DropdownMenuLabel className="text-xs">
                    <div className="flex items-center gap-2">
                      <UserRound size={14} className="text-cyan-400" />
                      <div>
                        <p className="font-semibold">{user.name}</p>
                        <p className="text-[10px] text-muted-foreground font-normal">{user.email}</p>
                      </div>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-border" />
                  <DropdownMenuItem
                    onClick={() => setPwdDialogOpen(true)}
                    className="text-xs cursor-pointer"
                    data-testid="change-password-menu-item"
                  >
                    <KeyRound size={13} className="mr-2" />
                    {t('account.change_password')}
                  </DropdownMenuItem>
                  {billing?.subscribed && (
                    <DropdownMenuItem
                      onClick={openPortal}
                      disabled={portalLoading}
                      className="text-xs text-teal-300 focus:text-teal-200 cursor-pointer"
                      data-testid="manage-subscription-button"
                    >
                      <CreditCard size={13} className="mr-2" />
                      {portalLoading ? t('billing.portal_opening') : t('billing.manage_subscription')}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="text-xs text-red-400 focus:text-red-300 cursor-pointer"
                    data-testid="logout-button"
                  >
                    <LogOut size={13} className="mr-2" />
                    {t('auth.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Mobile menu toggle */}
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </Button>
          </div>
        </div>
      </header>

      <TrialBanner />

      <ChangePasswordDialog open={pwdDialogOpen} onOpenChange={setPwdDialogOpen} />

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 pt-16 bg-background/95 backdrop-blur-sm md:hidden">
          <nav className="p-4 space-y-2">
            {navItems.map(({ path, label, icon: Icon }) => (
              <Button
                key={path}
                variant={isActive(path) ? 'secondary' : 'ghost'}
                className="w-full justify-start gap-3 h-12"
                onClick={() => {
                  navigate(path);
                  setMobileMenuOpen(false);
                }}
              >
                <Icon size={18} />
                {label}
              </Button>
            ))}
          </nav>
        </div>
      )}

      {/* Main Content */}
      <main className="main-content">
        {/* Disclaimer Banner */}
        <div className="bg-[hsl(210,18%,8%)] border-b border-border">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-2">
            <p className="text-[10px] md:text-xs text-muted-foreground text-center flex items-center justify-center gap-2">
              <Info size={12} className="shrink-0 text-cyan-400" />
              {t('disclaimer_banner')}
            </p>
          </div>
        </div>

        <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-6 md:py-10">
          {children}
        </div>

        <Footer />
      </main>
    </div>
  );
};

export default Layout;
